import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import {
    matchesStreamErrorCode as matchesStreamCode,
    parseStreamError,
    type StreamErrorPayload,
} from 'models'

export type { StreamErrorPayload }

type ErrorCode =
    | 'context_length_exceeded'
    | 'entity-not-found'
    | 'rate-limit-exceeded'
    | 'origin-not-allowed'
    | 'credit-exhausted'
    | 'conversation-blocked'
    | 'bad request'
    | 'internal-server-error'

function matchesCode(error: Error | undefined, code: ErrorCode): boolean {
    return error?.message?.toLowerCase().includes(code) ?? false
}

export function getStreamErrorPayload(
    error: Error | undefined,
): StreamErrorPayload | undefined {
    return parseStreamError(error?.message)?.payload
}

export function useChatbotError(error: MaybeRefOrGetter<Error | undefined>) {
    const isContextWindowError = computed(() =>
        matchesCode(toValue(error), 'context_length_exceeded'),
    )
    const isNotFoundError = computed(() =>
        matchesCode(toValue(error), 'entity-not-found'),
    )
    const isRateLimitError = computed(() =>
        matchesCode(toValue(error), 'rate-limit-exceeded'),
    )
    const isOriginNotAllowedError = computed(() =>
        matchesCode(toValue(error), 'origin-not-allowed'),
    )
    const isCreditExhaustedError = computed(() =>
        matchesCode(toValue(error), 'credit-exhausted'),
    )
    const isConversationBlockedError = computed(() =>
        matchesCode(toValue(error), 'conversation-blocked'),
    )
    const isBadRequestError = computed(() =>
        matchesCode(toValue(error), 'bad request'),
    )
    const isInternalServerError = computed(() =>
        matchesCode(toValue(error), 'internal-server-error'),
    )
    const isGatewayConfigError = computed(() =>
        matchesStreamCode(toValue(error)?.message, 'GATEWAY_CONFIG'),
    )

    /** Errors that trigger an automatic retry countdown */
    const isRefreshableError = computed(
        () =>
            !isContextWindowError.value &&
            !isNotFoundError.value &&
            !isOriginNotAllowedError.value &&
            !isCreditExhaustedError.value &&
            !isConversationBlockedError.value &&
            !isBadRequestError.value &&
            !isInternalServerError.value &&
            !isGatewayConfigError.value,
    )

    return {
        isContextWindowError,
        isNotFoundError,
        isRateLimitError,
        isOriginNotAllowedError,
        isCreditExhaustedError,
        isConversationBlockedError,
        isBadRequestError,
        isInternalServerError,
        isGatewayConfigError,
        isRefreshableError,
    }
}
